import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { setadvanceSettings, getadvanceSettings } from "../../network/cipAPI";

import InputBox from "../../components/InputBox";
import DupontRadio from "../../components/DupontRadio";
import DupontDialog from "../../components/DupontDialog";
import Dropdown from "../../components/Dropdown";
import { TextFieldValidator } from "../../components/InputBox/TextFieldValidator";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import { AdvanceSettingsConfig } from "./advancedSettings.config";
import { storeSettingsDefaults } from "../../Store/TemplateStore/template.action";
import "./index.scss";
import PopoverText from "../../components/PopoverText";
import InfoIconGreen from "../../assets/info-icon-green.svg";

const AdvanceSettings = ({
  isSettingsDialogOpen,
  handleSettingsDialogClose,
  setView,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const flow = useSelector((state) => state.flow);
  const pressure = useSelector((state) => state.pressure);
  const settingsConfigData = AdvanceSettingsConfig(t, pressure, flow);

  const defaultSettings = useSelector((state) => ({
    window: state.window,
    qp_pct_change_threshold: state.qp_pct_change_threshold,
    tmp_pct_change_threshold: state.tmp_pct_change_threshold,
    short_time_threshold: state.short_time_threshold,
    default_approach: state.default_approach,
    time1: state.time1,
    time2: state.time2,
    minimum_duration_between_cips: state.minimum_duration_between_cips,
    norm_tmp_threshold: state.norm_tmp_threshold,
    filtrate_flow_threshold: state.filtrate_flow_threshold,
    start_tmp_pct_change_threshold: state.start_tmp_pct_change_threshold,
    minimum_backwash_between_cips: state.minimum_backwash_between_cips,
    minimum_backwash_between_cebs: state.minimum_backwash_between_cebs,
    min_shutdown_days: state.min_shutdown_days,
    norm_tmp_threshold_cip: state.norm_tmp_threshold_cip,
    optimize_mode: state.optimize_mode,
    min_bwd_multiplier: state.min_bwd_multiplier,
    max_bwd_multiplier: state.max_bwd_multiplier,
    optimization_target: state.optimization_target,
  }));

  const originalDefaultSettings = {
    window: 7,
    qp_pct_change_threshold: 8,
    tmp_pct_change_threshold: 8,
    short_time_threshold: 180,
    default_approach: "Permeate Flow",
    time1: 3,
    time2: 60,
    minimum_duration_between_cips: 5,
    norm_tmp_threshold: pressure === "psig" ? 2.9 : 0.2,
    filtrate_flow_threshold: 0,
    start_tmp_pct_change_threshold: -10,
    minimum_backwash_between_cips: 20,
    minimum_backwash_between_cebs: 10,
    min_shutdown_days: 1,
    norm_tmp_threshold_cip: pressure === "psig" ? 4.35 : 0.3,
    optimize_mode: true,
    min_bwd_multiplier: 0.5,
    max_bwd_multiplier: 1.5,
    optimization_target: "Minimize Total Cost",
  };

  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [errors, setErrors] = useState({});
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    if (!isSettingsDialogOpen) return;
    const getAdvancedSettings = async () => {
      setShowOverlayLoader(true);
      try {
        const response = await getadvanceSettings();
        if (response && response.data) {
          setShowOverlayLoader(false);
          const transformedData = {
            window: response.data.window,
            qp_pct_change_threshold: response.data.qp_pct_change_threshold,
            tmp_pct_change_threshold: response.data.tmp_pct_change_threshold,
            short_time_threshold: response.data.short_time_threshold,
            default_approach: response.data.default_approach,
            time1: response.data.time1,
            time2: response.data.time2,
            minimum_duration_between_cips: response.data.minimum_duration_between_cips,
            norm_tmp_threshold: response.data.norm_tmp_threshold,
            filtrate_flow_threshold: response.data.filtrate_flow_threshold,
            start_tmp_pct_change_threshold:
              response.data.start_tmp_pct_change_threshold,
            minimum_backwash_between_cips:
              response.data.minimum_backwash_between_cips,
            minimum_backwash_between_cebs:
              response.data.minimum_backwash_between_cebs,
            min_shutdown_days: response.data.min_shutdown_days,
            norm_tmp_threshold_cip: response.data.norm_tmp_threshold_cip,
            optimize_mode:
              response.data.optimize_mode === "1" ||
              response.data.optimize_mode === true,
            min_bwd_multiplier: response.data.min_bwd_multiplier,
            max_bwd_multiplier: response.data.max_bwd_multiplier,
            optimization_target: response.data.optimization_target,
          };
          setSettings(transformedData);
          dispatch(storeSettingsDefaults(transformedData));
        }
      } catch (error) {
        console.log("Error fetching settings: ", error);
      } finally {
        setShowOverlayLoader(false);
      }
    };
    getAdvancedSettings();
  }, [isSettingsDialogOpen]);

  useEffect(() => {
    if (isSettingsDialogOpen) {
      setIsSaveDisabled(true);
    }
  }, [isSettingsDialogOpen]);

  const handleChange = (settingKey, value, minValue, maxValue) => {
    let errorMessage = null;
    // Generic validation for all fields
    if (value === "" || isNaN(parseFloat(value))) {
      errorMessage = `${t("advanceSettings.Rangemsg")} ${minValue} ${t(
        "advanceSettings.And"
      )} ${maxValue}`;
    } else if (
      minValue !== undefined &&
      maxValue !== undefined &&
      (parseFloat(value) < minValue || parseFloat(value) > maxValue)
    ) {
      errorMessage = `${t("advanceSettings.Rangemsg")} ${minValue} ${t(
        "advanceSettings.And"
      )} ${maxValue}`;
    } else if (minValue !== undefined && parseFloat(value) < minValue) {
      errorMessage = `${t("advanceSettings.lesserThan")} ${minValue}`;
    } else if (maxValue !== undefined && parseFloat(value) > maxValue) {
      errorMessage = `${t("advanceSettings.greaterThan")} ${maxValue}`;
    }
    const hasErrors = Object.values({
      ...errors,
      [settingKey]: errorMessage,
    }).some((error) => error !== null);

    setErrors({ ...errors, [settingKey]: errorMessage });
    const updatedSettings = { ...settings, [settingKey]: value };
    setSettings(updatedSettings);
    setIsSaveDisabled(hasErrors);
  };

  const handleRadioChange = (settingKey, value) => {
    const updatedSettings = {
      ...settings,
      [settingKey]: value === "true",
    };
    setSettings(updatedSettings);
    setIsSaveDisabled(false);
  };
  const handleDropdownchange = (settingKey, value) => {
    let errorMessage = null;
    if (value === "" || value === undefined || value === null) {
      errorMessage = t("advanceSettings.dropdownRequired");
    }
    const updatedErrors = { ...errors, [settingKey]: errorMessage };
    setErrors(updatedErrors);
    const updatedSettings = { ...settings, [settingKey]: value };
    setSettings(updatedSettings);
    // Disable save if any errors exist
    const hasErrors = Object.values(updatedErrors).some(
      (error) => error !== null
    );
    setIsSaveDisabled(hasErrors);
  };

  const preparePayload = () => {
    const payload = {
      window: settings.window,
      qp_pct_change_threshold: settings.qp_pct_change_threshold,
      tmp_pct_change_threshold: settings.tmp_pct_change_threshold,
      short_time_threshold: settings.short_time_threshold,
      default_approach: settings.default_approach,
      time1: settings.time1,
      time2: settings.time2,
      minimum_duration_between_cips: settings.minimum_duration_between_cips,
      norm_tmp_threshold: settings.norm_tmp_threshold,
      filtrate_flow_threshold: settings.filtrate_flow_threshold,
      start_tmp_pct_change_threshold: settings.start_tmp_pct_change_threshold,
      minimum_backwash_between_cips: settings.minimum_backwash_between_cips,
      minimum_backwash_between_cebs: settings.minimum_backwash_between_cebs,
      min_shutdown_days: settings.min_shutdown_days,
      norm_tmp_threshold_cip: settings.norm_tmp_threshold_cip,
      optimize_mode: settings.optimize_mode,
      min_bwd_multiplier: settings.min_bwd_multiplier,
      max_bwd_multiplier: settings.max_bwd_multiplier,
      optimization_target: settings.optimization_target,
    };

    return payload;
  };

  const handleSaveSettings = async () => {
    setShowOverlayLoader(true);
    try {
      const payload = preparePayload();
      const response = await setadvanceSettings(payload);
      if (response) {
        setShowOverlayLoader(false);
        setSettings(response);
        dispatch(storeSettingsDefaults(response));
        // Dispatch time1/time2 separately for instant update in MinimumSettings
        if (response.time1 !== undefined) {
          dispatch({
            type: "STORE_SETTINGS_DEFAULTS",
            payload: { time1: response.time1 },
          });
        }
        if (response.time2 !== undefined) {
          dispatch({
            type: "STORE_SETTINGS_DEFAULTS",
            payload: { time2: response.time2 },
          });
        }
        setView(2);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("advanceSettings.savemsg"),
          ""
        );
      } else {
        createNotification(ERROR_NOTIFICATION, response.detail.error, "");
      }
    } catch (error) {
      console.log("Error: ", error);
      createNotification(ERROR_NOTIFICATION, error.message, "");
    } finally {
      setShowOverlayLoader(false);
      handleSettingsDialogClose();
    }
  };

  const handleRestoreDefaults = () => {
    setSettings(originalDefaultSettings);
    setErrors({});
    setIsSaveDisabled(false);
  };

  const onClose = (action) => {
    if (action === "CONFIRM") {
      handleSaveSettings();
    } else if (action === "CANCEL") {
      setErrors({}); // Clear error messages on cancel
      handleSettingsDialogClose();
    } else if (action === "RESET") {
      handleRestoreDefaults();
    }
  };

  return (
    <>
      <DupontDialog
        open={isSettingsDialogOpen}
        onClose={onClose}
        title={t("analyseData.settings")}
        cancelBtnText={t("ValueStrings.cancel")}
        resetBtnText={t("advanceSettings.restoredefaults")}
        confirmBtnText={t("ValueStrings.confirm")}
        confirmDisabled={isSaveDisabled}
        maxwidth="xl"
        minWidth={775}
        isOverlayLoader={showOverlayLoader}
        customHeight={"520px"}
      >
        <div className="advance-setting-popup descriptionText font-14 ">
          <div className="padding10 position-relative  ">
            <div className="lightGrayBorder setting-page-padding marginBtm10">
              <div
                className="subheading__title"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{t("advanceSettings.cipAndShutdownIdentification")}</span>
                <PopoverText
                  descriptor={<img src={InfoIconGreen} alt="icon" />}
                  descriptionText={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("advanceSettings.datasetproductsused"),
                      }}
                    />
                  }
                />
              </div>

              {Object.keys(settingsConfigData.cipAndShutdownIdentification).map(
                (settingKey) => (
                  <div key={settingKey}>
                    {settingsConfigData.cipAndShutdownIdentification[settingKey]
                      .dropdown ? (
                      <div className="flex-between-center">
                        <label className="font-14 margin-radio">
                          {
                            settingsConfigData.cipAndShutdownIdentification[
                              settingKey
                            ].label
                          }
                        </label>
                        <div className="flex-between-center dropdown-width">
                          <Dropdown
                            value={
                              settings[settingKey] === undefined
                                ? defaultSettings[settingKey]
                                : settings[settingKey]
                            }
                            onChange={(value) =>
                              handleDropdownchange(settingKey, value)
                            }
                            options={
                              settingsConfigData.cipAndShutdownIdentification[
                                settingKey
                              ].options
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex-between-center">
                        <label className="font-14">
                          {
                            settingsConfigData.cipAndShutdownIdentification[
                              settingKey
                            ].label
                          }
                        </label>
                        <div className="dropdown-width marginBtm10">
                          <InputBox
                            value={
                              settings[settingKey] === undefined
                                ? defaultSettings[settingKey]
                                : settings[settingKey]
                            }
                            onChange={(value) =>
                              handleChange(
                                settingKey,
                                value,
                                settingsConfigData.cipAndShutdownIdentification[
                                  settingKey
                                ].minValue,
                                settingsConfigData.cipAndShutdownIdentification[
                                  settingKey
                                ].maxValue
                              )
                            }
                            validator={
                              settingKey === "start_tmp_pct_change_threshold" ||
                              settingKey === "filtrate_flow_threshold"
                                ? TextFieldValidator.NUMBER_WITH_DECIMALS_AND_NEGATIVE
                                : TextFieldValidator.NUMBER_TWO_DECIMAL
                            }
                            errorMsg={errors[settingKey]}
                            maxLength={10}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
            <div className="lightGrayBorder setting-page-padding marginBtm10">
              <div
                className="subheading__title"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  {t("advanceSettings.cipPredictionAndRecommendation")}
                </span>
                <PopoverText
                  descriptor={<img src={InfoIconGreen} alt="icon" />}
                  descriptionText={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("advanceSettings.differentproductlines"),
                      }}
                    />
                  }
                />
              </div>
              {Object.keys(
                settingsConfigData.cipPredictionAndRecommendation
              ).map((settingKey) => (
                <div key={settingKey}>
                  {settingsConfigData.cipPredictionAndRecommendation[settingKey]
                    .isBoolean ? (
                    <div className="flex-between-center">
                      <label className="font-14 margin-radio">
                        {
                          settingsConfigData.cipPredictionAndRecommendation[
                            settingKey
                          ].label
                        }
                      </label>
                      <div className="flex-between-center">
                        {Object.values(
                          settingsConfigData.cipPredictionAndRecommendation[
                            settingKey
                          ].options
                        ).map((option) => (
                          <div
                            className="marginBtm10 radio-button-container"
                            key={option.key}
                            onChange={(e) =>
                              handleRadioChange(settingKey, e.target.value)
                            }
                          >
                            <DupontRadio
                              text={option.name}
                              value={option.value}
                              checked={
                                settings[settingKey] === undefined
                                  ? defaultSettings[settingKey].toString() ===
                                    option.value
                                  : settings[settingKey].toString() ===
                                    option.value
                              }
                              name={settingKey}
                              disabled={true}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : settingsConfigData.cipPredictionAndRecommendation[
                      settingKey
                    ].dropdown ? (
                    <div className="flex-between-center">
                      <label className="font-14 margin-radio">
                        {
                          settingsConfigData.cipPredictionAndRecommendation[
                            settingKey
                          ].label
                        }
                      </label>
                      <div className="flex-between-center dropdown-width">
                        <Dropdown
                          value={
                            settings[settingKey] === undefined
                              ? defaultSettings[settingKey]
                              : settings[settingKey]
                          }
                          onChange={(value) =>
                            handleDropdownchange(settingKey, value)
                          }
                          options={
                            settingsConfigData.cipPredictionAndRecommendation[
                              settingKey
                            ].options
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div className=" flex-between-center">
                      <label className="font-14">
                        {
                          settingsConfigData.cipPredictionAndRecommendation[
                            settingKey
                          ].label
                        }
                      </label>
                      <div className="dropdown-width marginBtm10">
                        <InputBox
                          value={
                            settings[settingKey] === undefined
                              ? defaultSettings[settingKey]
                              : settings[settingKey]
                          }
                          onChange={(value) =>
                            handleChange(
                              settingKey,
                              value,
                              settingsConfigData.cipPredictionAndRecommendation[
                                settingKey
                              ].minValue,
                              settingsConfigData.cipPredictionAndRecommendation[
                                settingKey
                              ].maxValue
                            )
                          }
                          validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
                          errorMsg={errors[settingKey]}
                          maxLength={10}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DupontDialog>
    </>
  );
};

export default AdvanceSettings;
